import { useState, useCallback } from 'react';

interface ProposalData {
  value: string;
  isProposal: boolean;
}

interface UseProposalsReturn {
  proposals: Record<string, string>;
  isProposal: Record<string, boolean>;
  setProposalsFromResponse: (proposalsData: Record<string, ProposalData>) => Record<string, boolean>;
  acceptProposal: (field: string, onAccept: (value: string) => void) => void;
  rejectProposal: (field: string) => void;
  clearProposalStatus: (field: string) => void;
  hasProposal: (field: string) => boolean;
}

/**
 * Custom hook for managing AI-generated proposal state.
 * Handles proposal acceptance, rejection, and status tracking.
 */
export const useProposals = (): UseProposalsReturn => {
  const [proposals, setProposals] = useState<Record<string, string>>({});
  const [isProposal, setIsProposal] = useState<Record<string, boolean>>({});

  /**
   * Process proposals from API response and update state.
   * Returns the new isProposal state for use in form updates.
   */
  const setProposalsFromResponse = useCallback((proposalsData: Record<string, ProposalData>) => {
    const newProposals: Record<string, string> = {};
    const newIsProposal: Record<string, boolean> = {};

    Object.keys(proposalsData).forEach(key => {
      const proposal = proposalsData[key];
      newProposals[key] = proposal.value;
      newIsProposal[key] = proposal.isProposal;
    });

    setProposals(newProposals);
    setIsProposal(newIsProposal);

    return newIsProposal;
  }, []);

  /**
   * Accept a proposal and apply it to the form.
   * Calls onAccept callback with the proposal value.
   */
  const acceptProposal = useCallback((field: string, onAccept: (value: string) => void) => {
    if (proposals[field]) {
      onAccept(proposals[field]);
    }
    setIsProposal(prev => ({
      ...prev,
      [field]: false
    }));
    setProposals(prev => {
      const newProposals = { ...prev };
      delete newProposals[field];
      return newProposals;
    });
  }, [proposals]);

  /**
   * Reject a proposal without applying it.
   */
  const rejectProposal = useCallback((field: string) => {
    setIsProposal(prev => ({
      ...prev,
      [field]: false
    }));
    setProposals(prev => {
      const newProposals = { ...prev };
      delete newProposals[field];
      return newProposals;
    });
  }, []);

  /**
   * Clear proposal status when user edits a field.
   */
  const clearProposalStatus = useCallback((field: string) => {
    if (isProposal[field]) {
      setIsProposal(prev => ({
        ...prev,
        [field]: false
      }));
    }
  }, [isProposal]);

  /**
   * Check if a field has an active proposal.
   */
  const hasProposal = useCallback((field: string) => {
    return isProposal[field] && !!proposals[field];
  }, [isProposal, proposals]);

  return {
    proposals,
    isProposal,
    setProposalsFromResponse,
    acceptProposal,
    rejectProposal,
    clearProposalStatus,
    hasProposal
  };
};

export default useProposals;
